import { CompetenceBaseEx } from "@/server/backend-api";
import { createRef, RefObject, useRef, useState } from "react";
import { TopicCard, TopicCardProps } from "./TopicCard";
import { Assert } from "@/utils/assert";
import { mcn } from "@/utils";
import { clamp } from "@/utils/clamp";
import Button from "../Button";
import { IconActionChevronDown } from "@/components/Icons";

export interface CompetenceWithCardProps {
  competence: CompetenceBaseEx;
  cardProps: Pick<TopicCardProps, "classNames" | "likeOrDislike">;
}

export interface TopicCardContainerParams {
  competences: CompetenceWithCardProps[];
  onCardLikeClicked?: (competence: CompetenceBaseEx) => void;
  onCardDislikeClicked?: (competence: CompetenceBaseEx) => void;
  onCurrentClosestCardIndexChanged?: (newClosestCardIndex: number) => void;
  callToActionCard?: {
    text: string;
    buttonLabel: string;
    onClick: () => void;
    disabled: boolean;
  };
}

export function TopicCardContainer(props: TopicCardContainerParams) {
  const {
    competences,
    onCardDislikeClicked,
    onCardLikeClicked,
    callToActionCard,
  } = props;
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const divRef = createRef<HTMLDivElement>();
  const [closestCardIndex, setClosestCardIndex] = useState(0);
  const cardCount = competences.length + (callToActionCard ? 1 : 0);

  function scrollToCard(index: number) {
    const clampedIndex = clamp(0, cardCount - 1, index);
    const div = cardRefs.current[clampedIndex];
    Assert.notNull(div, "Card not found");
    div.scrollIntoView({
      behavior: "smooth",
      inline: "start",
      block: "nearest",
    });
  }

  return (
    <div className="">
      <div
        ref={divRef}
        className={mcn(
          "topic-card-container flex justify-start space-x-lg overflow-x-scroll snap-x snap-mandatory scrollbar-none scroll-smooth"
        )}
        onScroll={() => {
          const firstVisibleIndex = getClosestCardIndex(divRef, cardRefs);
          setClosestCardIndex(firstVisibleIndex);
          props.onCurrentClosestCardIndexChanged?.(firstVisibleIndex);
        }}
      >
        {competences.map((c, index) => {
          const isClosest = index === closestCardIndex;
          const scrollOnClick = !isClosest
            ? () => scrollToCard(index)
            : undefined;
          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            <div
              ref={(ref) => {
                cardRefs.current[index] = ref;
              }}
              onClick={scrollOnClick}
              className={mcn(
                "flex-shrink-0 snap-start w-full transition-opacity duration-100 group",
                "hover:opacity-100",
                !isClosest && "cursor-pointer",
                !isClosest &&
                  c.cardProps.likeOrDislike === undefined &&
                  "opacity-50",
                isClosest && "opacity-100"
              )}
              key={index}
            >
              <TopicCard
                classNames={mcn(
                  "transition-scale duration-300 border border-[transparent]",
                  !isClosest && "scale-95 group-hover:border-yellow-90",
                  isClosest && "scale-full"
                )}
                competence={c.competence}
                onLikeClicked={() => {
                  scrollToCard(index + 1);
                  onCardLikeClicked?.(c.competence);
                }}
                onDislikeClicked={() => {
                  scrollToCard(index + 1);
                  onCardDislikeClicked?.(c.competence);
                }}
                {...c.cardProps}
              />
            </div>
          );
        })}
        {callToActionCard && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
          <div
            ref={(ref) => {
              cardRefs.current[competences.length] = ref;
            }}
            onClick={() => scrollToCard(competences.length)}
            className={mcn(
              "flex-shrink-0 snap-start w-full transition-opacity duration-100 group",
              closestCardIndex !== cardCount - 1 && "opacity-50 cursor-pointer",
              closestCardIndex === cardCount - 1 && "opacity-100"
            )}
          >
            <div
              className={mcn(
                "h-[522px] rounded-3 bg-blue flex flex-col gap-y-lg items-center justify-center transition-scale duration-300 border border-[transparent]",
                closestCardIndex === cardCount - 1 && "scale-100",
                closestCardIndex !== cardCount - 1 &&
                  "scale-95 group-hover:border-yellow-90"
              )}
            >
              <span>{callToActionCard.text}</span>
              <Button
                label={callToActionCard.buttonLabel}
                onClick={callToActionCard.onClick}
                disabled={callToActionCard.disabled}
              />
            </div>
          </div>
        )}
      </div>
      <div className={"container flex justify-center py-md"}>
        <div className={"flex justiy-center gap-sm"}>
          <button
            className={mcn(
              "p-2",
              closestCardIndex === 0 && "invisible",
              "focus-visible:default-focus",
              "hover:opacity-50"
            )}
            onClick={() => scrollToCard(closestCardIndex - 1)}
          >
            <IconActionChevronDown className="w-xl h-xl rotate-90" />
            <span className="sr-only">Previuos topic</span>
          </button>
          <button
            className={mcn(
              "p-2",
              closestCardIndex === cardCount - 1 && "invisible",
              "focus-visible:default-focus",
              "hover:opacity-50"
            )}
            onClick={() => scrollToCard(closestCardIndex + 1)}
          >
            <IconActionChevronDown className="w-xl h-xl -rotate-90"/>
            <span className="sr-only">Next topic</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export const getClosestCardIndex = (
  containerRef: RefObject<HTMLDivElement>,
  cardRefs: RefObject<(HTMLDivElement | null)[]>
) => {
  const containerElement = containerRef.current;
  const cardElements = cardRefs.current;
  if (
    !containerElement ||
    !cardElements ||
    cardElements.length === 0 ||
    !cardElements[0]
  ) {
    return 0;
  }

  const firstLeft = cardElements[0].offsetLeft;
  const sortedByDistance = [...cardElements].sort((a, b) => {
    Assert.notNull(a, "a");
    Assert.notNull(b, "b");
    const aDistance = Math.abs(
      containerElement.scrollLeft - (a.offsetLeft - firstLeft)
    );
    const bDistance = Math.abs(
      containerElement.scrollLeft - (b.offsetLeft - firstLeft)
    );
    return aDistance - bDistance;
  });
  return cardElements.indexOf(sortedByDistance[0]);
};
